import { useEffect, useMemo, useState } from 'react';
import { Badge, Card, DataTable, InlineStack, Link, Pagination, Select, SkeletonBodyText, Text } from '@shopify/polaris';
import { formatToCurrency, getRequest } from '../../../../shared/components/functions';
import { CardHeading } from '../../../../shared/components/cards/CardHeading';
import { Delta } from './Delta';
import { businessUnitParam } from './BusinessUnitFilter';
import { formatInteger, formatPercent, sumBy, toNumber } from './analyticsHelpers';

// Ranked customers (revenue) or suppliers (costs) for one year, flippable through years.
export default function TopPartnersCard({ title, subtitle, endpoint, idKey, nameKey, entityLabel, years, invertDelta = false, businessUnitId = null }) {
  const [year, setYear] = useState(null);
  const [limit, setLimit] = useState('25');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const selectedYear = year && years.includes(year) ? year : years[years.length - 1];

  useEffect(() => {
    if (!selectedYear) return;
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      const result = await getRequest(`${endpoint}?year=${selectedYear}&limit=50${businessUnitParam(businessUnitId)}`);
      if (!cancelled) {
        setData(result);
        setLoading(false);
      }
    }
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [endpoint, selectedYear, businessUnitId]);

  const rows = useMemo(() => (Array.isArray(data?.rows) ? data.rows.slice(0, parseInt(limit)) : []), [data, limit]);
  const summary = data?.summary;
  const grandTotal = toNumber(summary?.total_incl);

  const concentration = useMemo(() => {
    if (!grandTotal || !Array.isArray(data?.rows)) return null;
    const top5 = sumBy(data.rows.slice(0, 5), 'total_incl') / grandTotal;
    const top10 = sumBy(data.rows.slice(0, 10), 'total_incl') / grandTotal;
    return { top5, top10 };
  }, [data, grandTotal]);

  let cumulative = 0;
  const tableRows = rows.map((row, idx) => {
    const total = toNumber(row.total_incl);
    cumulative += total;
    return [
      idx + 1,
      <InlineStack gap="200" blockAlign="center" wrap={false}>
        <Link removeUnderline url={`/companies/${row[idKey]}`}>
          {String(row[nameKey] || 'Unknown').substring(0, 40)}
        </Link>
        {Number(row.first_invoice_year) === Number(selectedYear) ? <Badge tone="new">new</Badge> : null}
      </InlineStack>,
      formatInteger(row.invoice_count),
      formatToCurrency(total),
      grandTotal ? formatPercent(total / grandTotal) : '—',
      grandTotal ? formatPercent(cumulative / grandTotal) : '—',
      <Delta current={total} previous={row.prev_total_incl} invert={invertDelta} />,
      formatToCurrency(toNumber(row.open_amount))
    ];
  });

  const yearIndex = years.indexOf(selectedYear);

  return (
    <Card>
      <CardHeading title={`${title} — ${selectedYear || ''}`} />
      {subtitle ? (
        <Text tone="subdued" as="p">
          {subtitle}
        </Text>
      ) : null}
      {summary ? (
        <Text tone="subdued" as="p">
          {formatInteger(summary.total_customers ?? summary.total_suppliers)} {entityLabel} · {formatInteger(summary.invoice_count)} invoices · {formatToCurrency(grandTotal)} incl. VAT
          {concentration ? ` · top 5 = ${formatPercent(concentration.top5)} · top 10 = ${formatPercent(concentration.top10)}` : ''}
        </Text>
      ) : null}
      <div style={{ margin: '12px 0' }}>
        <InlineStack align="space-between" blockAlign="center">
          <div style={{ width: '140px' }}>
            <Select
              label="Show"
              labelHidden
              options={[
                { label: 'Top 10', value: '10' },
                { label: 'Top 25', value: '25' },
                { label: 'Top 50', value: '50' }
              ]}
              value={limit}
              onChange={setLimit}
            />
          </div>
          <Pagination
            hasPrevious={yearIndex > 0}
            hasNext={yearIndex < years.length - 1}
            onPrevious={() => setYear(years[yearIndex - 1])}
            onNext={() => setYear(years[yearIndex + 1])}
            label={<b>{selectedYear}</b>}
          />
        </InlineStack>
      </div>
      {loading ? (
        <SkeletonBodyText lines={10} />
      ) : (
        <DataTable
          verticalAlign="middle"
          increasedTableDensity
          stickyHeader
          columnContentTypes={['numeric', 'text', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric', 'numeric']}
          headings={[<b>#</b>, <b>Name</b>, <b>Invoices</b>, <b>Total (incl. VAT)</b>, <b>Share</b>, <b>Cumulative</b>, <b>Δ vs prev. year</b>, <b>Open</b>]}
          rows={tableRows}
        />
      )}
    </Card>
  );
}
